import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Group, Modal, Paper, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { createVisit, linkVisitPoi, updateVisitData } from '../../api/resources';
import { isRetryAppropriate, qaCheckName, readVisitQa, retryInstructions } from '../../lib/qaChecks';
import type { VisitQa } from '../../types/api';

const OTHER = 'other';

/**
 * Retry: send an officer back by creating a NEW visit on the same case and
 * address. The visit being retried is never edited or reopened — the new one
 * records where it came from (`retryOfVisitId` on its data) and carries
 * instructions built from the reasons picked here.
 *
 * Only failed checks a retry can fix are offered; the rest are shown as a
 * warning, since a retry would only repeat them.
 */
export function RetryVisitModal({
    opened,
    onClose,
    visitId,
    caseId,
    visitType,
    poiId,
    qa,
}: {
    opened: boolean;
    onClose: () => void;
    visitId: string;
    caseId: string;
    visitType?: string;
    /** Address the retried visit targeted; the new visit goes to the same place. */
    poiId?: string;
    qa?: VisitQa | null;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [picked, setPicked] = useState<string[]>([]);
    const [otherText, setOtherText] = useState('');
    const [saving, setSaving] = useState(false);

    const failed = readVisitQa(qa).checks.filter((c) => c.status === 'failed');
    const retryable = failed.filter((c) => isRetryAppropriate(c.key));
    const notRetryable = failed.filter((c) => !isRetryAppropriate(c.key));
    const checkKeys = picked.filter((k) => k !== OTHER);
    const other = picked.includes(OTHER) ? otherText : '';
    const instructions = retryInstructions(t, checkKeys, other);
    const canSubmit = Boolean(checkKeys.length || other.trim());

    const submit = async () => {
        setSaving(true);
        let newId: string;
        try {
            const created = await createVisit(caseId, {
                ...(visitType ? { visitType } : {}),
                status: 'available',
                // Same address as the original; the backend only falls back to
                // the case's address when the original had none linked.
                ...(poiId ? { skipAutoVisitPoi: true } : {}),
            });
            newId = created.id;
        } catch (e) {
            setSaving(false);
            notifications.show({ color: 'red', message: (e as Error).message });
            return;
        }

        // The new visit EXISTS from here. A failed follow-up must not read as a
        // failed retry, or the reviewer retries again and creates a duplicate.
        const failedParts: string[] = [];
        if (poiId) {
            await linkVisitPoi(newId, { poiId }).catch(() =>
                failedParts.push(t('features.visits.retry.partAddress')),
            );
        }
        await updateVisitData(newId, {
            instructions,
            retryOfVisitId: visitId,
            retryReasons: picked,
        }).catch(() => failedParts.push(t('features.visits.retry.partInstructions')));

        void queryClient.invalidateQueries({ queryKey: ['visits'] });
        setSaving(false);
        notifications.show(
            failedParts.length
                ? {
                      color: 'yellow',
                      autoClose: false,
                      message: t('features.visits.retry.createdPartly', {
                          parts: failedParts.join(' & '),
                      }),
                  }
                : { color: 'green', message: t('features.visits.retry.created') },
        );
        onClose();
        navigate(`/visits/${newId}`);
    };

    return (
        <Modal opened={opened} onClose={onClose} title={t('features.visits.retry.title')} size="lg">
            <Stack>
                <Text size="sm" c="dimmed">
                    {t('features.visits.retry.hint')}
                </Text>

                <Checkbox.Group
                    label={t('features.visits.retry.reasonsLabel')}
                    description={t('features.visits.retry.reasonsHint')}
                    value={picked}
                    onChange={setPicked}
                >
                    <Stack gap="xs" mt="xs">
                        {retryable.map((c) => (
                            <Checkbox
                                key={c.key}
                                value={c.key}
                                label={qaCheckName(t, c.key)}
                                description={c.reason ?? t('features.visits.retry.failedOnVisit')}
                            />
                        ))}
                        <Checkbox value={OTHER} label={t('features.visits.retry.other')} />
                    </Stack>
                </Checkbox.Group>
                {picked.includes(OTHER) ? (
                    <Textarea
                        aria-label={t('features.visits.retry.otherLabel')}
                        placeholder={t('features.visits.retry.otherPlaceholder')}
                        minRows={2}
                        value={otherText}
                        onChange={(e) => setOtherText(e.currentTarget.value)}
                    />
                ) : null}

                {notRetryable.length > 0 ? (
                    <Alert color="orange" variant="light" title={t('features.visits.retry.notFixableTitle')}>
                        <Text size="sm">
                            {notRetryable.map((c) => qaCheckName(t, c.key)).join(', ')}.{' '}
                            {t('features.visits.retry.notFixableHint')}
                        </Text>
                    </Alert>
                ) : null}

                {instructions ? (
                    <Stack gap={4}>
                        <Text size="sm" fw={600}>
                            {t('features.visits.retry.previewLabel')}
                        </Text>
                        <Paper withBorder p="sm" radius="md">
                            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                                {instructions}
                            </Text>
                        </Paper>
                    </Stack>
                ) : null}

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button onClick={() => void submit()} loading={saving} disabled={!canSubmit}>
                        {t('features.visits.retry.submit')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
